import { Component } from '@theme/component';
import { buildSectionSelector, morphSection, sectionRenderer } from '@theme/section-renderer';
import { RecentlyViewed } from '@theme/recently-viewed-products';

class RecentlyViewedProducts extends Component {
  /**
   * @type {IntersectionObserver}
   */
  #intersectionObserver = new IntersectionObserver(
    (entries, observer) => {
      if (!entries[0]?.isIntersecting) return;

      observer.disconnect();
      this.#loadProducts();
    },
    { rootMargin: '0px 0px 400px 0px' }
  );

  connectedCallback() {
    super.connectedCallback();

    if (window.Shopify?.designMode) {
      this.dataset.productsLoaded = 'true';
      return;
    }

    this.#intersectionObserver.observe(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#intersectionObserver.disconnect();
  }

  /**
   * Build the search URL used to hydrate recently viewed products.
   * Mirrors predictive-search.js so search.results is populated by product IDs.
   * @param {string[]} productIds
   */
  #buildSearchUrl(productIds) {
    const url = new URL(Theme.routes.search_url, window.location.origin);
    url.searchParams.set('q', productIds.map((id) => `id:${id}`).join(' OR '));
    url.searchParams.set('type', 'product');
    url.searchParams.set('resources[type]', 'product');
    return url;
  }

  async #loadProducts() {
    const { sectionId, excludeProductId, maxProducts } = this.dataset;

    if (!sectionId) {
      this.#hide();
      return;
    }

    const limit = Number(maxProducts) || 4;
    const viewedProducts = RecentlyViewed.getProducts()
      .map(String)
      .filter((id) => id && id !== String(excludeProductId))
      .slice(0, limit);

    if (viewedProducts.length === 0) {
      this.#hide();
      return;
    }

    const url = this.#buildSearchUrl(viewedProducts);

    try {
      // Prefer the product-template instance ID so static block settings (card layout) match.
      const sectionHTML = await sectionRenderer.getSectionHTML(sectionId, false, url);
      const sectionSelector = buildSectionSelector(sectionId);

      if (sectionHTML?.includes(sectionSelector)) {
        this.dataset.productsLoaded = 'true';
        morphSection(sectionId, sectionHTML, { mode: 'hydration', injectStylesheet: true });
        this.#finalize();
        return;
      }

      await this.#injectFromFilenameRender(url);
    } catch (error) {
      console.error('Recently viewed products error:', error);
      try {
        await this.#injectFromFilenameRender(url);
      } catch (fallbackError) {
        console.error('Recently viewed products fallback error:', fallbackError);
        this.#hide();
      }
    }
  }

  /**
   * When the product-template section ID is not available on /search,
   * render by filename and copy the product list into this instance.
   * @param {URL} url
   */
  async #injectFromFilenameRender(url) {
    const filenameHTML = await sectionRenderer.getSectionHTML('recently-viewed-products', false, url);
    const fragment = new DOMParser().parseFromString(filenameHTML, 'text/html');
    const rendered = fragment.querySelector('recently-viewed-products');
    const newLists = rendered?.querySelectorAll('.resource-list');

    if (!newLists?.length || !rendered?.querySelector('[data-has-products="true"]')) {
      this.#hide();
      return;
    }

    this.querySelectorAll('.resource-list').forEach((node) => node.remove());

    const content = this.querySelector('.section-resource-list__content');
    const mount = content?.parentElement || this.querySelector('.section-resource-list') || this;
    newLists.forEach((list) => mount.appendChild(list.cloneNode(true)));

    this.dataset.productsLoaded = 'true';
    this.#finalize();
  }

  #finalize() {
    requestAnimationFrame(() => {
      const itemCount = this.querySelectorAll('.resource-list__item').length;

      if (itemCount === 0) {
        this.#hide();
        return;
      }

      this.classList.remove('hidden');
    });
  }

  #hide() {
    this.classList.add('hidden');
    this.dataset.error = 'No recently viewed products';
  }
}

if (!customElements.get('recently-viewed-products')) {
  customElements.define('recently-viewed-products', RecentlyViewedProducts);
}
